import {useCallback, useEffect, useRef, useState} from 'react';
import {fetchJurisdictionClassifications, fetchJurisdictionGuidance, type JurisdictionClassifications, type JurisdictionGuidanceResult} from './fenzsafeJurisdictionGuidance';
import './FenzSafeJurisdictionGuidancePanel.css';

/**
 * Availability is checked once when the panel mounts (Issue #497 item 4), so the panel never
 * presents an apparently functional "Check jurisdiction guidance" action that can only lead to an
 * environment-not-configured dead end. `coming_soon` means the reviewed dataset is not configured
 * here; `check_failed` means the availability read itself failed and is retryable. Neither is the
 * same as "configured but no reference matches this case" (NO_RESEARCH_REFERENCE), which is shown
 * as a result after a real check.
 */
type Availability = 'checking' | 'available' | 'coming_soon' | 'check_failed';

/**
 * FenzSafe's jurisdiction/legal-resource integration seam UI (Issue #164 founder continuation
 * directive, item 2). A thin, self-contained panel over the real, existing
 * ProFenz\Fulfillment\ProofRuleReferenceLookup contract -- see
 * docs/ai/FENZSAFE_JURISDICTION_GUIDANCE.md for the full design and its one disclosed limitation
 * (the underlying dataset is scoped to process-of-service rules; FenzSafe matters don't carry
 * enough structured context to auto-derive most of its required dimensions, so this panel asks the
 * customer to supply them explicitly rather than guessing).
 *
 * Every result this panel can ever show is honestly labeled "needs review" -- the one reviewed
 * package this codebase can load is deliberately entirely MANUAL_REVIEW_REQUIRED/trusted_rule=null
 * (a fail-closed research-only dataset), so `confirmed` is correctly always false today. This panel
 * never presents a result as a confirmed local rule, never fabricates a citation, and never invents
 * a working "send to Mission Control" action that doesn't exist -- it says plainly that a person
 * needs to review this, matching the founder's "never give unsupported legal advice" requirement.
 */
type Props = {
  /**
   * Reports a short, human-readable summary whenever a checked result needs a person to review
   * (or clears it back to null once nothing outstanding remains) -- FenzSafeApp.tsx feeds this
   * into Holmes Jr.'s tip so the guide surfaces it without duplicating this panel's own logic.
   */
  onEscalation?: (message: string | null) => void;
};

function escalationSummary(result: JurisdictionGuidanceResult): string | null {
  if (!result.mustEscalate) return null;
  if (result.status === 'NO_RESEARCH_REFERENCE') return 'no research reference exists yet for the selected combination.';
  if (result.status === 'CASE_CONTEXT_REQUIRED') return 'more information is needed before this can be checked.';
  if (result.status === 'REFERENCE_SET_TOO_BROAD') return 'too many candidate references matched -- ask Holmes Jr. or Mission Control for help narrowing this down.';
  return 'a real, cited reference was found but is not yet a confirmed local rule.';
}

export function FenzSafeJurisdictionGuidancePanel({onEscalation}: Props) {
  const [availability, setAvailability] = useState<Availability>('checking');
  const [formOpen, setFormOpen] = useState(false);
  const [classifications, setClassifications] = useState<JurisdictionClassifications | null>(null);
  const firstFieldRef = useRef<HTMLSelectElement>(null);
  const retryRef = useRef<HTMLButtonElement>(null);
  const mountedRef = useRef(true);
  const [jurisdictionId, setJurisdictionId] = useState('');
  const [matterType, setMatterType] = useState('');
  const [targetType, setTargetType] = useState('');
  const [serviceMethod, setServiceMethod] = useState('');
  const [serviceOutcome, setServiceOutcome] = useState('');
  const [issuingCourtId, setIssuingCourtId] = useState('');
  const [divisionId, setDivisionId] = useState('');
  const [result, setResult] = useState<JurisdictionGuidanceResult | null>(null);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const checkAvailability = useCallback(async (): Promise<Availability> => {
    setAvailability('checking');
    let next: Availability;
    try {
      const loaded = await fetchJurisdictionClassifications();
      if (!mountedRef.current) return 'checking';
      setClassifications(loaded);
      next = loaded.configured ? 'available' : 'coming_soon';
    } catch {
      next = 'check_failed';
    }
    if (mountedRef.current) setAvailability(next);
    return next;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void checkAvailability();
    return () => {
      mountedRef.current = false;
    };
  }, [checkAvailability]);

  function open() {
    if (availability !== 'available' || !classifications) return;
    setFormOpen(true);
    // Move focus to the first field so the customer sees what the action revealed (#497 item 5).
    requestAnimationFrame(() => firstFieldRef.current?.focus());
  }

  async function retry() {
    const next = await checkAvailability();
    // Keep focus somewhere meaningful: the retry control if it is still shown, otherwise the
    // now-available action is reachable as the next control in the panel.
    if (next === 'check_failed') requestAnimationFrame(() => retryRef.current?.focus());
  }

  async function submit() {
    setSubmitError('');
    setSubmitting(true);
    try {
      const guidance = await fetchJurisdictionGuidance({jurisdictionId, matterType, targetType, serviceMethod, serviceOutcome, issuingCourtId, divisionId});
      setResult(guidance);
      onEscalation?.(escalationSummary(guidance));
    } catch {
      setSubmitError('This could not be checked right now. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = jurisdictionId !== '' && matterType !== '' && targetType !== '' && serviceMethod !== '' && serviceOutcome !== '' && issuingCourtId.trim() !== '' && divisionId.trim() !== '';

  return (
    <section className="fsa-panel" aria-labelledby="fsa-jurisdiction-heading">
      <h2 id="fsa-jurisdiction-heading">Jurisdiction &amp; rules guidance</h2>
      {availability === 'checking' && (
        <p className="fsa-package-status" role="status">
          Checking whether jurisdiction guidance is available…
        </p>
      )}
      {availability === 'coming_soon' && (
        <p className="fsa-package-status" role="status">
          Jurisdiction guidance is coming soon. When it's ready, it will show reviewed, cited references for your court -- never a guess. There's nothing you need to do here now.
        </p>
      )}
      {availability === 'check_failed' && (
        <>
          <p className="fsa-package-status" role="status">
            We couldn't check whether jurisdiction guidance is available right now. Your matter is not affected.
          </p>
          <button ref={retryRef} type="button" className="fsa-secondary" onClick={() => void retry()}>
            Try again
          </button>
        </>
      )}
      {availability === 'available' && !formOpen && (
        <>
          <p className="fsa-package-status">Check what's known about court/filing requirements for this matter's jurisdiction, sourced from ProFenz's reviewed legal-research contract -- never invented.</p>
          <button type="button" className="fsa-secondary" onClick={open}>
            Check jurisdiction guidance
          </button>
        </>
      )}
      {availability === 'available' && formOpen && classifications && (
        <div className="fjg-form">
          <label className="fjg-field">
            State/jurisdiction
            <select ref={firstFieldRef} value={jurisdictionId} onChange={(event) => setJurisdictionId(event.target.value)}>
              <option value="">Select…</option>
              {classifications.classifications.jurisdiction_id.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="fjg-field">
            Matter type
            <select value={matterType} onChange={(event) => setMatterType(event.target.value)}>
              <option value="">Select…</option>
              {classifications.classifications.matter_type.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="fjg-field">
            Recipient type
            <select value={targetType} onChange={(event) => setTargetType(event.target.value)}>
              <option value="">Select…</option>
              {classifications.classifications.target_type.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="fjg-field">
            Service method
            <select value={serviceMethod} onChange={(event) => setServiceMethod(event.target.value)}>
              <option value="">Select…</option>
              {classifications.classifications.service_method.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="fjg-field">
            Outcome
            <select value={serviceOutcome} onChange={(event) => setServiceOutcome(event.target.value)}>
              <option value="">Select…</option>
              {classifications.serviceOutcomes.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="fjg-field">
            Court name
            <input type="text" value={issuingCourtId} onChange={(event) => setIssuingCourtId(event.target.value)} placeholder="e.g. Wake County District Court" />
          </label>
          <label className="fjg-field">
            Division/county
            <input type="text" value={divisionId} onChange={(event) => setDivisionId(event.target.value)} placeholder="e.g. Small Claims" />
          </label>
          <button type="button" className="fsa-secondary" disabled={!canSubmit || submitting} onClick={() => void submit()}>
            {submitting ? 'Checking…' : 'Check guidance'}
          </button>
          {submitError && (
            <p className="fjg-error" role="alert">
              {submitError}
            </p>
          )}
          {result && (
            <div className="fjg-result" role="status">
              {result.mustEscalate && (
                <p className="fjg-escalate">
                  This needs a person to review -- nothing here is a confirmed local rule. {result.status === 'NO_RESEARCH_REFERENCE' ? "No research reference exists yet for this exact combination." : ''}
                </p>
              )}
              {result.citations.length > 0 && (
                <ul className="fjg-citations">
                  {result.citations.map((citation, index) => (
                    <li key={citation.rule_id ?? index}>
                      {citation.governing_citation && <span className="fjg-citation-text">{citation.governing_citation}</span>}
                      {citation.primary_source_citation && (
                        <a href={citation.primary_source_citation} target="_blank" rel="noreferrer">
                          Source
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
